import React, { useEffect, useState } from 'react';
import DateField from '@srm/ui/date-field';
import { dateToIsoDate, isoToDate } from '@srm/ui/lib/dateFormValue';
import { portalOverlayClassName } from '@srm/ui/lib/portalOverlay';

type DateCellEditorProps = {
    name: string;
    value: unknown;
    minDate?: string;
    maxDate?: string;
    workCalendar?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    setValue: (next: Date | null) => void;
};

/**
 * Date editor для ячейки таблицы.
 *
 * Локальный state нужен потому, что DataGrid обновляет своё `p.value` асинхронно
 * (только после blur ячейки). Без локального состояния выбор даты в календаре
 * визуально применялся бы лишь при потере фокуса.
 *
 * Классы `portalOverlayClassName` помечают popover и крестик-очистку как часть
 * текущего редактора, чтобы грид не завершал редактирование при кликах в них
 * (см. utils/portalOverlay).
 *
 * `variant="unstyled"` убирает собственные границы Mantine DateInput — внутри
 * ячейки таблицы DataGrid сам отрисовывает границы.
 */
const DateCellEditor: React.FC<DateCellEditorProps> = ({
    name,
    value,
    minDate,
    maxDate,
    workCalendar,
    readOnly,
    placeholder,
    setValue,
}) => {
    const [local, setLocal] = useState<string | null>(dateToIsoDate(value));

    useEffect(() => {
        setLocal(dateToIsoDate(value));
    }, [value]);

    return (
        <DateField
            readOnly={readOnly}
            name={name}
            variant="unstyled"
            value={local}
            minDate={minDate}
            maxDate={maxDate}
            workCalendar={workCalendar}
            placeholder={placeholder}
            onChange={(next: string | null) => {
                setLocal(next);
                setValue(isoToDate(next));
            }}
            popoverProps={{ classNames: { dropdown: portalOverlayClassName } }}
            clearButtonProps={{ className: portalOverlayClassName, tabIndex: -1 }}
        />
    );
};

export default DateCellEditor;
